import { BaseService } from './common/baseService';
import { supabase } from "@/integrations/supabase/client";
import type { Bill } from './common/types';

export interface CreateBillData {
  supplier_id: string;
  bill_number?: string;
  amount: number;
  currency?: string;
  status?: string;
  issue_date?: string;
  due_date?: string;
  description?: string;
  discount_percentage?: number;
  tax_rate?: number | null;
  file_path?: string | null;
  grand_total?: number;
}

export interface UpdateBillData extends Partial<CreateBillData> {
  id: string;
}

class BillsService extends BaseService {
  constructor() {
    super('bills');
  }

  async getBills(): Promise<Bill[]> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .order('updated_at', { ascending: false });
    });
  }

  async getBillById(id: string): Promise<Bill | null> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id(name, id, email, phone)
        `)
        .eq('id', id)
        .maybeSingle();
    });
  }

  async createBill(billData: CreateBillData): Promise<Bill> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .insert([billData])
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .single();
    });
  }

  async updateBill({ id, ...updateData }: UpdateBillData): Promise<Bill> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .single();
    });
  }

  async deleteBill(id: string): Promise<void> {
    return this.delete(id);
  }

  async getBillsByStatus(status: string): Promise<Bill[]> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .eq('status', status)
        .order('issue_date', { ascending: false });
    });
  }

  async getBillsBySupplier(supplierId: string): Promise<Bill[]> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .eq('supplier_id', supplierId)
        .order('issue_date', { ascending: false });
    });
  }

  async getOverdueBills(): Promise<Bill[]> {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id(name, id)
        `)
        .in('status', ['draft', 'sent'])
        .lt('due_date', new Date().toISOString().split('T')[0])
        .order('due_date', { ascending: true });
    });
  }

  async markBillAsPaid(id: string, paidDate?: string): Promise<Bill> {
    const updateData: any = {
      status: 'paid',
      paid_date: paidDate || new Date().toISOString().split('T')[0]
    };
    return this.updateBill({ id, ...updateData });
  }

  async getBillMetrics() {
    return this.executeQuery(async () => {
      return await supabase
        .from('bills')
        .select('status, amount, currency, issue_date, due_date, paid_date')
        .order('issue_date', { ascending: false });
    });
  }
}

export const billsService = new BillsService();